import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  message,
  Modal,
  Row,
  Select,
  Switch,
  Tag,
  Typography,
} from "antd";
import { CloseOutlined, SaveOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { getFirebaseIDToken } from "../../../../../functions/firebase/firebase";
import { updateBatchCountedStock } from "../../../../../functions/api/stock";
import { CHEFS_FARM, COMPANY_COUNTRY_CODE, GROWY } from "../../../../../constants/CompanyConstants";
import QuantityControl from "components/shared-components/QuantityControl";
import dayjs from "dayjs";
import { PRODUCT_COMPANY_GROUPS, PRODUCT_GROUPS } from "../../../../../constants/ProductConstant";
import StockChangeReasonModal from "./StockChangeReasonModal";

const { Title, Text } = Typography;
const { Option } = Select;

const BatchStockUpdateModal = ({ visible, onCancel, stockDates, products, categories, stockData }) => {
  const [modal, contextHolder] = Modal.useModal();
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [showAllProducts, setShowAllProducts] = useState(true);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [modifiedProducts, setModifiedProducts] = useState({});
  const [loading, setLoading] = useState(false);
  const [resetCounter, setResetCounter] = useState(0);
  const [stockReasonModalOpen, setStockReasonModalOpen] = useState(false);
  const [pendingBatchUpdate, setPendingBatchUpdate] = useState(null);

  useEffect(() => {
    if (visible) {
      // Reset form state when modal becomes visible
      form.setFieldsValue({
        stockSource: CHEFS_FARM,
        productGroup: "all",
        productCategory: "all",
        countDate: dayjs(),
        warehouse: "Cold Large",
        showAllProducts: true,
      });
      setShowAllProducts(true);
      setSelectedProducts([]);
      setSearchValue("");
      setModifiedProducts({});
    }
  }, [visible, form]);

  const handleToggleAllProducts = (checked) => {
    const hasChanges = Object.keys(modifiedProducts).length > 0;

    if (hasChanges) {
      Modal.confirm({
        title: t("unsaved_changes_title"),
        content: t("unsaved_changes_content"),
        okText: t("discard"),
        cancelText: t("stay"),
        onOk: () => {
          // Discard changes and proceed with toggle
          setModifiedProducts({});
          setResetCounter((c) => c + 1);
          setShowAllProducts(checked);
          form.setFieldsValue({ showAllProducts: checked });
          if (!checked) {
            setSelectedProducts([]);
          }
        },
        onCancel: () => {
          // Revert the Switch visual state in the Form
          form.setFieldsValue({ showAllProducts: !checked });
        },
      });
      return;
    }

    setShowAllProducts(checked);
    if (!checked) {
      setSelectedProducts([]);
    }
  };

  const handleProductSelection = (selected) => {
    setSelectedProducts(selected);
  };

  const removeProduct = (productId) => {
    setSelectedProducts((prev) => prev.filter((id) => id !== productId));
    // Also remove from modified products if it exists
    setModifiedProducts((prev) => {
      const updated = { ...prev };
      delete updated[productId];
      return updated;
    });
  };

  const updateModifiedProducts = (productId, newValue) => {
    setModifiedProducts((prev) => ({
      ...prev,
      [productId]: newValue,
    }));
  };

  const handleClose = () => {
    if (Object.keys(modifiedProducts).length > 0) {
      Modal.confirm({
        title: t("unsaved_changes_title"),
        content: t("unsaved_changes_content"),
        okText: t("discard"),
        cancelText: t("stay"),
        onOk: () => {
          // Discard changes and reset any manual input states
          setModifiedProducts({});
          setResetCounter((c) => c + 1);
          onCancel();
        },
      });
    } else {
      // Also ensure any manual modes are reset even if there are no changes
      setResetCounter((c) => c + 1);
      onCancel();
    }
  };

  const handleSave = () => {
    const values = form.getFieldsValue();

    if (Object.keys(modifiedProducts).length === 0) {
      message.warning(t("no_changes_to_save"));
      return;
    }

    const formattedDate = values.countDate.format("DDMMYYYY");

    // Prepare confirmation details
    const confirmationDetails = (
      <div>
        <p>{t("confirm_batch_update_stock")}</p>
        <ul>
          <li>
            <strong>{t("stock_source")}:</strong> {values.stockSource}
          </li>
          <li>
            <strong>{t("count_date")}:</strong> {values.countDate.format("DD-MM-YYYY")}
          </li>
          <li>
            <strong>{t("warehouse")}:</strong> {values.warehouse}
          </li>
          <li>
            <strong>{t("products_to_update")}:</strong> {Object.keys(modifiedProducts).length}
          </li>
        </ul>
      </div>
    );

    modal.confirm({
      title: t("confirm_save"),
      content: confirmationDetails,
      okText: t("save"),
      cancelText: t("cancel"),
      onOk: () => {
        // Store pending update and show reason modal
        setPendingBatchUpdate({ dateId: formattedDate });
        setStockReasonModalOpen(true);
      },
    });
  };

  const handleStockReasonConfirm = async (isIncomingProducts) => {
    if (!pendingBatchUpdate) return;

    try {
      setLoading(true);
      const token = await getFirebaseIDToken();

      // Prepare the updates
      const updates = Object.entries(modifiedProducts).map(([productId, countedStock]) => ({
        productId,
        newCountedStock: countedStock,
      }));

      await updateBatchCountedStock(
        updates,
        pendingBatchUpdate.dateId,
        COMPANY_COUNTRY_CODE,
        token,
        isIncomingProducts,
      );

      message.success(t("batch_update_success"));
      setStockReasonModalOpen(false);
      setPendingBatchUpdate(null);
      onCancel(); // Close the modal after successful save
    } catch (error) {
      console.error("Error updating batch stock:", error);
      message.error(t("batch_update_error"));
    } finally {
      setLoading(false);
    }
  };

  const handleStockReasonCancel = () => {
    setStockReasonModalOpen(false);
    setPendingBatchUpdate(null);
  };

  const getFilteredAndSortedProducts = () => {
    if (!products) return [];
    const selectedCompany = form.getFieldValue("stockSource");
    const selectedProductGroup = form.getFieldValue("productGroup");
    const selectedProductCategory = form.getFieldValue("productCategory");
    return products
      .filter((product) => product.productInSeason === true)
      .filter((product) => {
        // If a company is selected, ensure product.company matches it
        return !selectedCompany || product.company === selectedCompany;
      })
      .filter((product) => {
        return (
          selectedProductGroup === "all" ||
          (product.productGroup && product.productGroup.toLowerCase() === selectedProductGroup.toLowerCase())
        );
      })
      .filter((product) => {
        return (
          selectedProductCategory === "all" ||
          (product.productCategory && product.productCategory.toLowerCase() === selectedProductCategory.toLowerCase())
        );
      })
      .sort((a, b) => a.productName.localeCompare(b.productName));
  };

  const renderProductsList = () => {
    const selectedDate = form.getFieldValue("countDate");
    if (!selectedDate) return null;

    const dateId = selectedDate.format("DDMMYYYY");
    const filteredProducts = getFilteredAndSortedProducts();

    let productsToRender = [];

    if (showAllProducts) {
      productsToRender = filteredProducts;
    } else {
      productsToRender = filteredProducts.filter((product) => selectedProducts.includes(product.productId));
    }

    return (
      <>
        {contextHolder}
        <div style={{ maxHeight: "400px", overflowY: "auto", marginTop: "8px" }}>
          {productsToRender.map((product) => {
            const originalStock = stockData[dateId]?.[product.productId]?.countedStock || 0;
            const isModified = modifiedProducts[product.productId] !== undefined;
            const currentValue =
              modifiedProducts[product.productId] !== undefined ? modifiedProducts[product.productId] : originalStock;

            return (
              <StyledCard key={product.productId} style={isModified ? { borderLeft: "3px solid #1890ff" } : {}}>
                <ProductRow>
                  {!showAllProducts && (
                    <Button
                      type="text"
                      size="small"
                      icon={<CloseOutlined />}
                      onClick={() => removeProduct(product.productId)}
                      style={{ marginRight: "5px" }}
                    />
                  )}
                  <ProductInfo>
                    <Text strong>{product.productName}</Text>
                    <br />
                    <Text type="secondary">{product.productId}</Text>
                    {isModified && (
                      <Tag color="blue" style={{ marginLeft: 8 }}>
                        {t("modified")}
                      </Tag>
                    )}
                  </ProductInfo>

                  <StockControls>
                    <Text>
                      {t("current")}: {originalStock}
                      {currentValue !== originalStock && (
                        <span
                          style={{
                            marginLeft: 6,
                            color: currentValue - originalStock > 0 ? "#3f8600" : "#cf1322",
                          }}
                        >
                          (
                          {currentValue - originalStock > 0
                            ? `+${currentValue - originalStock}`
                            : currentValue - originalStock}
                          )
                        </span>
                      )}
                    </Text>

                    <QuantityControl
                      key={`${product.productId}-${resetCounter}`}
                      productId={product.productId}
                      currentQuantity={currentValue}
                      onIncrease={() => {
                        const base =
                          modifiedProducts[product.productId] !== undefined
                            ? modifiedProducts[product.productId]
                            : originalStock;
                        updateModifiedProducts(product.productId, base + 1);
                      }}
                      onDecrease={() => {
                        const current =
                          modifiedProducts[product.productId] !== undefined
                            ? modifiedProducts[product.productId]
                            : originalStock;
                        const next = Math.max(0, current - 1);
                        updateModifiedProducts(product.productId, next);
                      }}
                      onManualInput={(productId, value) => {
                        updateModifiedProducts(productId, value);
                      }}
                    />
                  </StockControls>
                </ProductRow>
              </StyledCard>
            );
          })}

          {productsToRender.length === 0 && (
            <div style={{ textAlign: "center", padding: "20px" }}>
              <Text type="secondary">{t("no_products_selected")}</Text>
            </div>
          )}
        </div>
      </>
    );
  };

  return (
    <Modal
      title={t("batch_update_stock")}
      open={visible}
      onCancel={handleClose}
      width={700}
      destroyOnHidden
      footer={[
        <Button key="back" onClick={handleClose}>
          {t("close")}
        </Button>,
        <Button key="submit" type="primary" onClick={handleSave} loading={loading} icon={<SaveOutlined />}>
          {t("save")}
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical">
        <Row gutter={16}>
          <Col xs={12} sm={8}>
            <Form.Item name="stockSource" label={t("stock_from")}>
              <Select
                onChange={() => {
                  // Clear selections and modified state when switching company
                  setSelectedProducts([]);
                  setModifiedProducts({});
                }}
              >
                {PRODUCT_COMPANY_GROUPS.map((group) => (
                  <Option key={group.value} value={group.value}>
                    {group.value === CHEFS_FARM ? t("chefs_farm") : group.value === GROWY ? t("growy") : group.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={12} sm={8}>
            <Form.Item name="countDate" label={t("count_date")}>
              <DatePicker
                style={{ width: "100%" }}
                onChange={(date) => {
                  // Clear modified products when date changes
                  setModifiedProducts({});
                }}
                disabledDate={(current) => {
                  // Only allow dates that exist in stockDates
                  if (!stockDates || stockDates.length === 0) return false;

                  // Enable dates that match our stockDates
                  return !stockDates.some(
                    (date) => dayjs(date.dateUnix * 1000).format("YYYY-MM-DD") === current.format("YYYY-MM-DD"),
                  );
                }}
              />
            </Form.Item>
          </Col>

          <Col xs={12} sm={8}>
            <Form.Item name="warehouse" label={t("warehouse")}>
              <Select>
                <Option value="Cold Large">{t("cold_large")}</Option>
                <Option value="Seeding" disabled>
                  {t("seeding")}
                </Option>
                <Option value="Harvest" disabled>
                  {t("harvest")}
                </Option>
                <Option value="Packing" disabled>
                  {t("packing")}
                </Option>
                <Option value="Warehouse" disabled>
                  {t("warehouse")}
                </Option>
              </Select>
            </Form.Item>
          </Col>

          <Col xs={12} sm={8}>
            <Form.Item name="productGroup" label={t("product_group")}>
              <Select
                onChange={() => {
                  // Clear selections when switching product groups
                  setSelectedProducts([]);
                }}
              >
                <Option value="all">{t("all_groups")}</Option>
                {PRODUCT_GROUPS.map((group) => (
                  <Option key={group.value} value={group.value}>
                    {t(group.label)}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={12} sm={8}>
            <Form.Item name="productCategory" label={t("product_category")}>
              <Select
                onChange={() => {
                  // Clear selections when switching product groups
                  setSelectedProducts([]);
                }}
              >
                <Option value="all">{t("all_categories")}</Option>
                {!!categories &&
                  categories.map((elm) => (
                    <Option key={elm} value={elm}>
                      {elm}
                    </Option>
                  ))}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={12} sm={8}>
            <Form.Item name="showAllProducts" label={t("show_all_products")} valuePropName="checked">
              <Switch checkedChildren={t("yes")} unCheckedChildren={t("no")} onChange={handleToggleAllProducts} />
            </Form.Item>
          </Col>

          {!showAllProducts && (
            <Col xs={24} sm={12}>
              <Form.Item>
                <Select
                  mode="tags"
                  showSearch
                  placeholder={t("search_products")}
                  optionFilterProp="label"
                  onChange={handleProductSelection}
                  style={{ width: "100%" }}
                  filterOption={(input, option) => {
                    if (!option.label || !input) return false;
                    return option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                  }}
                  tagRender={() => null}
                  value={selectedProducts}
                >
                  {getFilteredAndSortedProducts().map((product) => (
                    <Option
                      key={product.productId}
                      value={product.productId}
                      label={`${product.productName} (${product.productId})`}
                    >
                      {product.productName} ({product.productId})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          )}
        </Row>
      </Form>

      <Divider />

      <Title level={5}>
        {t("products")} ({t("inSeason")})
      </Title>
      {renderProductsList()}

      <StockChangeReasonModal
        open={stockReasonModalOpen}
        onCancel={handleStockReasonCancel}
        onConfirm={handleStockReasonConfirm}
      />
    </Modal>
  );
};

const StyledCard = styled(Card)`
  margin-bottom: 8px;
  .ant-card-body {
    padding: 10px;
  }
`;

const ProductRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const ProductInfo = styled.div`
  flex: 1;
`;

const StockControls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export default BatchStockUpdateModal;
